import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { BaseEntity } from './BaseEntity';

export interface FlipperEntityParams
{
    type: string,
    x: number,
    y: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
    props: {
        power?: number
        angle?: number
    }
}

export class FlipperEntity extends BaseEntity
{
    public ramp: Sprite;
    public arm: Sprite;
    public params!: FlipperEntityParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;
    public launched = false;

    private wheelLeft = false;
    private wheelRight = false;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.culling = true;

        this.arm = Sprite.from(Shared.assets.getTexture('flipper-flipper'));
        this.arm.anchor.set(0.1, 0.5);
        this.view.addChild(this.arm);
        this.arm.x = 24 - 20;
        this.arm.y = -9;

        this.ramp = Sprite.from(Shared.assets.getTexture('flipper-ramp'));
        this.ramp.anchor.set(0.5);
        this.view.addChild(this.ramp);
        this.ramp.x = -20;
        this.ramp.y = -5;
    }

    public build(params: FlipperEntityParams): void
    {
        this.physics.clearParts();
        this.params = params;

        // RAMP & ARM
        const body = new p2.Body({
            position: [this.params.x, -this.params.y],
            mass: 0,
            angle: 0,
        });

        body.harmPlayer = true;

        const absx = Math.abs(this.params.sx);
        const absy = Math.abs(this.params.sy);

        const verts = [
            [-220 * this.params.sx, -20 * absy],
            [-20 * this.params.sx, 25 * absy],
            [20 * this.params.sx, 25 * absy],
            [220 * this.params.sx, 5 * absy],
            [220 * this.params.sx, -25 * absy],
            [-220 * this.params.sx, -25 * absy],
            [-220 * this.params.sx, -20 * absy],
        ];

        body.fromPolygon(verts, {
            optimalDecomp: false,
            skipSimpleCheck: true,
            removeCollinearPoints: 0.01,
        });

        for (const shape of body.shapes)
        {
            shape.collisionGroup = PhysicsCollisionGroup.OBJECT;
            shape.collisionMask = PhysicsCollisionGroup.VEHICLE;
            shape.material = Shared.game.physics.contact.getDefaultGroundMaterial();
            // shape.material = Shared.game.physics.contact.getMaterial({
            //     name: `flipper`,
            //     friction: 0.1,
            // });
        }

        // TRIGGER
        const trigger = new p2.Body({
            position: [this.params.x + (120 * this.params.sx), -this.params.y + (50 * this.params.sy)],
            mass: 0,
            angle: 0,
        });

        const shape = new p2.Box({
            width: 130 * absx,
            height: 130 * absy,
        });

        shape.collisionGroup = PhysicsCollisionGroup.LEVEL;
        shape.collisionMask = PhysicsCollisionGroup.VEHICLE;
        shape.sensor = true;
        trigger.addShape(shape);
        trigger.onBeginContact = this.onTriggerBeginContact.bind(this);

        // Add parts and set group rotation
        this.physics.addParts(body, trigger);
        this.physics.setAngle(this.params.r);

        this.view.scale.set(this.params.sx, this.params.sy);

        this.culling.updateBounds();
    }

    public levelRespawn(): void
    {
        this.reactivate();
    }

    public levelReset(): void
    {
        this.reactivate();
    }

    protected onTriggerBeginContact(other: p2.Body): void
    {
        if (this.launched || !other.physicsComponent) return;
        const game = this.scene as Game;

        if (other.physicsComponent?.entity === game.vehicle.wheelLeft) this.wheelLeft = true;
        if (other.physicsComponent?.entity === game.vehicle.wheelRight) this.wheelRight = true;
        this.launch();
    }

    private launch(): void
    {
        if (!this.wheelLeft || !this.wheelRight) return;
        if (this.launched) return;
        this.launched = true;

        const game = this.scene as Game;
        const direction = this.params.sx < 0 ? -1 : 1;
        const power = this.params.props.power || 550;
        const angle = this.params.props.angle || 0.3;
        const forceAngle = -this.params.r - (angle * direction);
        const force = [-Math.sin(forceAngle) * power, Math.cos(forceAngle) * power];

        game.vehicle.chassis.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.chassis.physics.mainBody.velocity[1] *= 0.5;
        game.vehicle.wheelLeft.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.wheelLeft.physics.mainBody.velocity[1] *= 0.5;
        game.vehicle.wheelRight.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.wheelRight.physics.mainBody.velocity[1] *= 0.5;

        game.vehicle.chassis.physics.mainBody.applyImpulse(force);
        game.vehicle.wheelLeft.physics.mainBody.applyImpulse(force);
        game.vehicle.wheelRight.physics.mainBody.applyImpulse(force);

        this.animate();

        game.soundSystem.playSfx('launcher', 1);

        game.physics.timer.dispatchAfter(3, () => this.reactivate());
    }

    protected animate(): void
    {
        this.game?.tween.to(this.arm, { rotation: -0.5, duration: 0.1, ease: 'sine.in' });

        this.game?.physics.timer.dispatchAfter(0.5, () =>
        {
            this.game?.tween.to(this.arm, { rotation: 0, duration: 0.3, ease: 'sine.inOut' });
        });
    }

    protected reactivate(): void
    {
        this.game?.tween.kill(this.arm);
        this.arm.rotation = 0;
        this.wheelLeft = false;
        this.wheelRight = false;
        this.launched = false;
    }
}

export class FlipperFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'flipper';
    }

    public static create(game: Game, data: Record<string, any>): void
    {
        const entity = game.pool.get(FlipperEntity) as FlipperEntity;

        entity.build(data as FlipperEntityParams);

        game.level.addEntity(entity);
    }
}

